// Minnesotaänger Mini App — message bubble.
// Restored from Web A (4a2c0277) message-rendering contract so the baseline
// Message.scss applies: `Message message-list-item` → `.message-content-wrapper.can-select-text`
// → `.message-content.{contentClassName}[dir=auto]` (contentClassName from the
// restored buildContentClassName, e.g. `message-content text has-shadow
// has-solid-background has-appendix no-footer`) → `.content-inner[dir=auto]`
// (sender name at top when withSenderName) → `.text-content.clearfix.with-meta`
// with the timestamp inside the bubble, and the bubble-tail MessageAppendix
// when contentClassName carries `has-appendix`.
// Kept from the v1 rewrite: inline edit (telegram-tt has no such feature) and
// the unified six-item context menu shared by every bubble.

import { memo, useEffect, useRef, useState } from '../../../lib/teact/teact';

import type { ElementRef } from '../../../lib/teact/teact';

import type { MiniAppMessage } from '../../../api/miniapp/adapter';
import { getMessageHtmlId } from '../../../api/miniapp/adapter';

import type { MiniAppPendingOperation } from '../../../global/miniapp/types';

import { getActions } from '../../../global';

import buildClassName from '../../../util/buildClassName';
import renderText from '../../common/helpers/renderText';
import useContextMenuHandlers from '../../../hooks/useContextMenuHandlers';
import useLastCallback from '../../../hooks/useLastCallback';

import Button from '../../ui/Button';

import MessageContextMenu from './MessageContextMenu';
import MessageMeta from './MessageMeta';
import MessageAppendix from './MessageAppendix';
import { buildContentClassName } from './helpers/buildContentClassName';

import './Message.scss';

type OwnProps = {
  chatId: string;
  message: MiniAppMessage;
  withSenderName?: boolean;
  senderName?: string;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  isLastInList: boolean;
  // Selection mode (§3.5) and pending ops (§3.4) come from shared state —
  // the bubble only projects them.
  selectedMessageIds: string[];
  pendingOperations: MiniAppPendingOperation[];
};

function Message({
  chatId,
  message,
  withSenderName,
  senderName,
  isFirstInGroup,
  isLastInGroup,
  isLastInList,
  selectedMessageIds,
  pendingOperations,
}: OwnProps) {
  const {
    // v1 (fix-list E): reply targets are event_ids (l0 rowids).
    setReplyingToEvent,
    toggleMessageSelection,
    copyMessage,
    rerollMessage,
    editMessage,
    deleteMessage,
  } = getActions();

  const ref = useRef<HTMLDivElement>();
  const editTextareaRef = useRef<HTMLTextAreaElement>();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');

  const {
    isContextMenuOpen,
    contextMenuAnchor,
    handleContextMenu,
    handleContextMenuClose,
    handleContextMenuHide,
  } = useContextMenuHandlers(ref, isEditing);

  useEffect(() => {
    if (isEditing) {
      editTextareaRef.current?.focus();
      editTextareaRef.current?.setSelectionRange(editTextareaRef.current.value.length, editTextareaRef.current.value.length);
    }
  }, [isEditing]);

  const isOwn = message.isOutgoing;

  // Selection is a visible mode (§3.5): while any message is selected, tapping
  // a bubble toggles its selection; the highlight class projects shared state.
  const isSelected = selectedMessageIds.includes(String(message.id));
  const isSelectionMode = selectedMessageIds.length > 0;
  const isPendingOp = pendingOperations.some((op) => op.targetEventId === message.mini.event_id);

  const contentClassName = buildContentClassName(message, undefined, { isLastInGroup });
  const withAppendix = contentClassName.includes('has-appendix');

  const handleClick = useLastCallback(() => {
    if (!isSelectionMode) return;
    toggleMessageSelection({ chatId, messageId: String(message.id) });
  });

  const handleReply = useLastCallback(() => {
    // v1 (fix-list E): the stable event_id (l0 rowid), not the platform
    // message id — the bridge resolves it server-side.
    setReplyingToEvent({ chatId, eventId: message.mini.event_id });
  });

  const handleCopy = useLastCallback(() => {
    copyMessage({ chatId, messageId: String(message.id) });
  });

  const handleSelect = useLastCallback(() => {
    toggleMessageSelection({ chatId, messageId: String(message.id) });
  });

  const handleReroll = useLastCallback(() => {
    rerollMessage({ chatId, eventId: message.mini.event_id });
  });

  const handleEdit = useLastCallback(() => {
    setEditText(message.mini.text);
    setIsEditing(true);
  });

  const handleDelete = useLastCallback(() => {
    deleteMessage({ chatId, eventId: message.mini.event_id });
  });

  const handleEditSave = useLastCallback(() => {
    const text = editText.trim();
    if (text && text !== message.mini.text) {
      editMessage({ chatId, eventId: message.mini.event_id, text });
    }
    setIsEditing(false);
  });

  const handleEditCancel = useLastCallback(() => {
    setIsEditing(false);
  });

  const handleEditKeyDown = useLastCallback((e: { key: string; shiftKey: boolean; preventDefault: () => void }) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  });

  function renderSenderName() {
    return (
      <div className="message-title" dir="ltr">
        <div className="message-title-name-container interactive" dir="ltr">
          <div className="message-title-name">
            <span className="sender-title" dir="auto">
              {renderText(senderName, ['br'])}
            </span>
          </div>
        </div>
        <div className="title-spacer" />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      id={getMessageHtmlId(message.id)}
      data-message-id={message.id}
      className={buildClassName(
        'Message message-list-item',
        isFirstInGroup && 'first-in-group',
        isLastInGroup && 'last-in-group',
        isLastInList && 'last-in-list',
        isOwn && 'own',
        isContextMenuOpen && 'has-menu-open',
        isSelected && 'message-selected',
        'allow-selection',
        // 基线 MessageList.scss 的 `.Message:not(.open) { opacity: 0; scale(0.8) }`
        // 要求每条消息带 `open` class。telegram-tt 由 useShowTransition（appearanceOrder
        // 错峰 + setTimeout(markShown)）在挂载后添加；v1 无淡入动画机制，渲染即终态
        // （等价 Web A `noAppearanceAnimation` 全开时最新消息的首帧行为）。
        'open',
      )}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      <div className="bottom-marker" data-message-id={message.id} />
      <div className="message-content-wrapper can-select-text">
        <div className={contentClassName} dir="auto">
          <div className="content-inner" dir="auto">
            {isEditing ? (
              <div className="message-edit">
                <textarea
                  ref={editTextareaRef}
                  className="message-edit-textarea"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                />
                <div className="message-edit-actions">
                  <Button size="tiny" onClick={handleEditSave}>保存</Button>
                  <Button size="tiny" color="secondary" onClick={handleEditCancel}>取消</Button>
                </div>
              </div>
            ) : (
              <>
                {withSenderName && renderSenderName()}
                <div className="text-content clearfix with-meta" dir="auto">
                  {/* v1 has no emoji engine (src/lib/twemojiRegex.ts is an empty
                      v1-cuto file), so only the line-break filter is passed. */}
                  {renderText(message.content.text?.text ?? message.mini.text, ['br'])}
                  <MessageMeta message={message.mini} />
                </div>
              </>
            )}
          </div>
          {withAppendix && <MessageAppendix isOwn={isOwn} />}
        </div>
      </div>
      {contextMenuAnchor && (
        <MessageContextMenu
          message={message.mini}
          isPendingOp={isPendingOp}
          isOpen={isContextMenuOpen}
          anchor={contextMenuAnchor}
          onReply={handleReply}
          onCopy={handleCopy}
          onSelect={handleSelect}
          onReroll={handleReroll}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onClose={handleContextMenuClose}
          onCloseAnimationEnd={handleContextMenuHide}
        />
      )}
    </div>
  );
}

export default memo(Message);
